import { createSelector } from "reselect"
import { find, filter as _filter, get, chain, isEmpty, mapValues, pickBy, reduce, merge } from "lodash"
import { getDefaultCurrencyId } from "modules/account-root/selectors"
import { unsupportedCurrencyWarning } from "helpers/currency"
import { getModelSupportedCurrencies } from "selectors/modelMetadata"
import { getCountries, getCurrencies, getClassificationFacetTrees } from "modules/securities-finder-facets/selectors"
import { createQueryStringSelector } from "selectors/queryString"
import { classificationsMap } from "helpers/classification"
import { ensureValidClassificationFilters } from "helpers/securities-search"
import { getSelectedDate } from "./dates"

const EMPTY_OBJECT = {}

export const getFilter = createQueryStringSelector("filter", EMPTY_OBJECT)
export const getEditingFilter = (state) => get(state, "modules.marketToolsSecuritiesFinder.editing", EMPTY_OBJECT)
const getCurrencyOverride = (_, props) => props.currency

export const getRawCountryFacets = createQueryStringSelector("country", EMPTY_OBJECT)
const getRawCurrencyFacets = createQueryStringSelector("currency", EMPTY_OBJECT)
const getRawClassificationFacets = createQueryStringSelector("classifications", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getClassificationsValues = (state) => get(state, "global.data.classificationsValues.data")

// Map facets from querystring format to the format expected by downstream selectors
export const getCountryFacets = createSelector(
  [getRawCountryFacets],
  (countryFacets) => reduce(countryFacets, (facets, country) => ({ ...facets, [country]: true }), {}),
)

export const getCurrencyFacets = createSelector(
  [getRawCurrencyFacets],
  (currencyFacets) => reduce(currencyFacets, (facets, currency) => ({ ...facets, [currency]: true }), {}),
)

/**
 * Maps classification facets from querystring to the format expected by downstream selectors.
 *
 * The format of the querystring parameter is:
 *
 * {
 *   [classificationId]: {
 *    [tierId]: [facetId, ...]
 *   }
 * }
 *
 * The format expected by downstream selectors is:
 *
 * {
 *   [classificationId]: {
 *     [facetId]: {
 *       tier: tierId,
 *       selected: true
 *     }
 * }
 */
export const getClassificationFacets = createSelector(
  [getRawClassificationFacets],
  (classificationFacets) => reduce(classificationFacets, (allFacets, tier, tierId) => {
    const tierFacets = reduce(tier, (allTierFacets, facets, facetTypeId) => {
      const tier = reduce(facets, (allFacets, facetId) => merge(allFacets, {
        [facetId]: {
          tier: facetTypeId,
          selected: true,
        },
      }), {})
      return merge(allTierFacets, { [tierId]: tier })
    }, {})
    return merge(allFacets, tierFacets)
  }, {}),
)

export const getClassificationsTiers = createSelector(
  [getClassifications, getSelectedDate],
  classificationsMap,
)

export const getValidFilter = createSelector(
  [getFilter, getClassifications, getClassificationsValues],
  ensureValidClassificationFilters,
)

export const getFilterId = createSelector(
  [getFilter],
  filter => filter ? JSON.stringify(filter) : null,
)

export const getClassificationFilteredFacets = createSelector(
  [getClassificationFacets, getClassificationFacetTrees],
  (classificationFacets, availableClassifications) => {
    return mapValues(classificationFacets, (c, cId) => {
      return pickBy(c, (f, fId) => get(availableClassifications, [cId, f.tier, "facets", fId]))
    })
  },
)

export const getFacetedFilter = createSelector(
  [getValidFilter, getRawCountryFacets, getRawCurrencyFacets, getClassificationFilteredFacets, getCountries, getCurrencies],
  (filter, countryFacets, currencyFacets, classificationFacets, availableCountries, availableCurrencies) => {
    if (!filter) {
      return null
    }

    let query = { ...filter }

    const countries = _filter(countryFacets, c => find(availableCountries, ac => ac.id === c))
    if (countries.length) {
      query.country = {
        in: countries,
      }
    }

    const currencies = _filter(currencyFacets, c => find(availableCurrencies, ac => ac.id === c))
    if (currencies.length) {
      query.currency = {
        in: currencies,
      }
    }

    const classificationsFacetsFilter = chain(classificationFacets)
      .map((classification, id) => ({
        id,
        in: chain(classification)
          .map((facet, facetId) => ({ value: facetId, ...facet }))
          .map(({ value, tier }) => ({ value, tier }))
          .value(),
      }))
      .filter((cFilter) => !isEmpty(get(cFilter, "in")))
      .value()

    let existingClassifications = get(query, "classification", [])
    if (!Array.isArray(existingClassifications)) {
      existingClassifications = [existingClassifications]
    }

    const classificationFilters = [
      ...existingClassifications,
      ...classificationsFacetsFilter,
    ]

    query = {
      ...query,
      ...(!isEmpty(classificationFilters) && { classification: classificationFilters }),
    }

    return isEmpty(query) ? null : query
  },
)

export const getFacetedFilterId = createSelector(
  [getFacetedFilter],
  filter => filter ? JSON.stringify(filter) : null,
)

export const getCurrency = createSelector(
  [getDefaultCurrencyId, getCurrencyOverride],
  (accountCurrencyId, currencyOverride) => currencyOverride || accountCurrencyId,
)

export const getHeaderCurrency = createSelector(
  [getModelSupportedCurrencies, getCurrency],
  (supportedCurrencies, currency) => unsupportedCurrencyWarning(supportedCurrencies, currency) ? null : currency,
)
